import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCMSPage } from '../hooks/useCMSPage';
import { membershipBenefits } from '../mockData'; // Fallback data
import { SEOHead } from '../components/SEOHead';
import './Membership.css';

export const Membership = () => {
  const { 
    loading, 
    error, 
    getSectionContent, 
    getSectionBlocks, 
    isSectionVisible 
  } = useCMSPage('/membership');

  // Get CMS content with fallbacks
  const heroContent = getSectionContent('MEMBERSHIP_HERO') || {
    title: 'Join The Clubhouse',
    subtitle: 'Become part of the Golf Nuts community. Get exclusive access, member giveaways, GHIN tracking, and connect with fellow golf enthusiasts.'
  };

  const ctaContent = getSectionContent('MEMBERSHIP_SIGNUP_CTA') || {
    heading: 'Start Your Free Membership',
    description: 'Join thousands of golf enthusiasts in our community. Create your account to access the Clubhouse app, track your handicap, and compete on leaderboards.',
    primaryButtonText: 'Create Your Account',
    primaryButtonLink: '/clubhouse/register',
    secondaryText: 'Already a member?',
    secondaryLinkText: 'Sign in here',
    secondaryLink: '/clubhouse/login'
  };

  // Get benefits from CMS or fallback to mockData
  const benefitsBlocks = getSectionBlocks('MEMBERSHIP_BENEFITS');
  const benefits = benefitsBlocks.length > 0 
    ? benefitsBlocks
        .filter(block => block.isVisible !== false)
        .map(block => ({
          title: block.content?.title || '',
          description: block.content?.description || ''
        }))
    : membershipBenefits;

  // Get FAQ items from CMS
  const faqBlocks = getSectionBlocks('MEMBERSHIP_FAQ');
  const faqItems = faqBlocks.length > 0 
    ? faqBlocks
        .filter(block => block.isVisible !== false)
        .map(block => ({
          question: block.content?.question || '',
          answer: block.content?.answer || ''
        }))
    : [
        { question: 'Is membership really free?', answer: 'Yes! Our basic membership is completely free. We\'ll never charge you without your explicit consent. In the future, we may offer premium tiers with additional perks.' },
        { question: 'What do I get as a member?', answer: 'Members get access to our Clubhouse app, exclusive giveaways, The Turn Newsletter, GHIN tracking with leaderboards, priority access to events, and future partner discounts.' },
        { question: 'Can I cancel anytime?', answer: 'Absolutely. You can unsubscribe from emails or cancel your membership at any time, no questions asked. We want you here because you love golf, not because you feel stuck.' },
        { question: 'How do member events work?', answer: 'We organize member scrambles, course meetups, and trips throughout the year. As a member, you\'ll get early notice and priority registration for all events.' }
      ];

  // Show loading skeleton while fetching CMS data
  if (loading) {
    return (
      <div className="membership-page">
        <div className="membership-hero">
          <div className="container">
            <div className="skeleton-title"></div>
            <div className="skeleton-text"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="membership-page">
      <SEOHead
        title="Membership"
        description="Join the Golf Nuts Clubhouse — exclusive perks, member-only content, handicap tracking, leaderboards, and a community of golf-obsessed friends."
        path="/membership"
      />
      {/* Hero Section */}
      {isSectionVisible('MEMBERSHIP_HERO') && (
        <div className="membership-hero">
          <div className="container">
            <h1 className="display-large">{heroContent.title}</h1>
            <p className="hero-subtitle-text">{heroContent.subtitle}</p>
          </div>
        </div>
      )}

      {/* Sign Up CTA */}
      {isSectionVisible('MEMBERSHIP_SIGNUP_CTA') && (
        <section className="signup-section">
          <div className="container">
            <div className="signup-card">
              <div className="signup-content">
                <h2 className="heading-1">{ctaContent.heading}</h2>
                <p className="body-large">{ctaContent.description}</p>
                
                <div className="signup-cta-buttons">
                  <Link 
                    to={ctaContent.primaryButtonLink || '/clubhouse/register'} 
                    className="btn-primary btn-large"
                  >
                    {ctaContent.primaryButtonText || 'Create Your Account'}
                  </Link>
                  {ctaContent.secondaryText && (
                    <p className="login-prompt">
                      {ctaContent.secondaryText}{' '}
                      <Link 
                        to={ctaContent.secondaryLink || '/clubhouse/login'} 
                        className="login-link"
                      >
                        {ctaContent.secondaryLinkText || 'Sign in here'}
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benefits List */}
      {isSectionVisible('MEMBERSHIP_BENEFITS') && benefits.length > 0 && (
        <section className="benefits-section">
          <div className="container">
            <div className="benefits-container">
              <h2 className="heading-1">Member Benefits</h2>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={`member-benefit-${benefit.title || 'item'}-${index}`} className="benefit-item">
                    <div className="benefit-icon-wrapper">
                      <Check size={24} strokeWidth={3} />
                    </div>
                    <div className="benefit-content">
                      <h3 className="benefit-title">{benefit.title}</h3>
                      <p className="benefit-description">{benefit.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {isSectionVisible('MEMBERSHIP_FAQ') && faqItems.length > 0 && (
        <section className="faq-section">
          <div className="container">
            <h2 className="heading-1">Frequently Asked Questions</h2>
            <div className="faq-grid">
              {faqItems.map((faq, index) => (
                <div key={`faq-${faq.question?.slice(0, 30) || 'item'}-${index}`} className="faq-item">
                  <h3 className="heading-3">{faq.question}</h3>
                  <p className="body-medium">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
